import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useResponsive from '@/shared/hooks/useResponsive';

import Button from '@/shared/components/button/Button';

import AnswerDigitalBgMobile from '@/features/timeGame/assets/images/answer-digital-mobile.svg?react';
import AnswerDigitalBg from '@/features/timeGame/assets/images/answer-digital.svg?react';

import '@/features/timeGame/styles/digital-clock-answer.scss';

interface DigitalClockAnswerProps {
	hours: number;
	minutes: number;
	onHoursChange: (hours: number) => void;
	onMinutesChange: (minutes: number) => void;
}

const DIGIT_HEIGHT_LANDSCAPE = 74;
const DIGIT_HEIGHT_PORTRAIT = 52;
const DIGIT_HEIGHT_LANDSCAPE_SMALL = 40;
const SCROLL_RESET_DELAY = 120;
const HOURS_REPEAT_COUNT = 7;
const MINUTES_REPEAT_COUNT = 7;
const normalizeIndex = (index: number, length: number) => ((index % length) + length) % length;

const DigitalClockAnswer = ({ hours, minutes, onHoursChange, onMinutesChange }: DigitalClockAnswerProps) => {
	const { isPortrait, isTablet } = useResponsive();

	const hoursScrollRef = useRef<HTMLDivElement>(null);
	const minutesScrollRef = useRef<HTMLDivElement>(null);
	const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const hasInitializedRef = useRef(false);
	const [isScrolling, setIsScrolling] = useState(false);

	const DIGIT_HEIGHT = isPortrait
		? DIGIT_HEIGHT_PORTRAIT
		: isTablet
		? DIGIT_HEIGHT_LANDSCAPE_SMALL
		: DIGIT_HEIGHT_LANDSCAPE;
	const BackgroundComponent = isPortrait ? AnswerDigitalBgMobile : AnswerDigitalBg;

	const getNearestIndex = useCallback(
		(
			value: number,
			length: number,
			repeatCount: number,
			scrollRef: React.RefObject<HTMLDivElement | null>,
			fallbackOffset: number,
		) => {
			if (!scrollRef.current) {
				return { index: fallbackOffset + value, distance: 0 };
			}

			const scrollTop = scrollRef.current.scrollTop;
			const currentIndex = Math.round(scrollTop / DIGIT_HEIGHT);
			const occurrences = Array.from({ length: repeatCount }, (_, i) => value + i * length);

			const closest = occurrences.reduce<{
				index: number;
				distance: number;
			}>(
				(acc, candidate) => {
					const distance = Math.abs(candidate - currentIndex);
					if (distance < acc.distance) {
						return { index: candidate, distance };
					}
					return acc;
				},
				{
					index: occurrences[0],
					distance: Math.abs(occurrences[0] - currentIndex),
				},
			);

			return closest;
		},
		[DIGIT_HEIGHT],
	);

	// Generate arrays for hours (0-23) and minutes (0-59)
	const hoursArray = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);
	const minutesArray = useMemo(() => Array.from({ length: 60 }, (_, i) => i), []);

	// Repeat the range so users can scroll past 00 to see 23 again (and vice versa)
	const repeatedHours = useMemo(
		() => Array.from({ length: HOURS_REPEAT_COUNT }, () => hoursArray).flat(),
		[hoursArray],
	);
	const repeatedMinutes = useMemo(
		() => Array.from({ length: MINUTES_REPEAT_COUNT }, () => minutesArray).flat(),
		[minutesArray],
	);

	const middleHoursOffset = Math.floor(HOURS_REPEAT_COUNT / 2) * hoursArray.length;
	const middleMinutesOffset = Math.floor(MINUTES_REPEAT_COUNT / 2) * minutesArray.length;

	const clearScrollTimeout = () => {
		if (scrollTimeoutRef.current) {
			clearTimeout(scrollTimeoutRef.current);
			scrollTimeoutRef.current = null;
		}
	};

	const markScrolling = () => {
		setIsScrolling(true);
		clearScrollTimeout();
		scrollTimeoutRef.current = setTimeout(() => {
			setIsScrolling(false);
			scrollTimeoutRef.current = null;
		}, SCROLL_RESET_DELAY);
	};

	const scrollToIndex = useCallback(
		(ref: React.RefObject<HTMLDivElement | null>, index: number, behavior: ScrollBehavior = 'smooth') => {
			if (!ref.current || index < 0) return;
			const targetTop = index * DIGIT_HEIGHT;

			if (Math.abs(ref.current.scrollTop - targetTop) < 1) {
				return;
			}

			ref.current.scrollTo({
				top: targetTop,
				behavior,
			});
		},
		[DIGIT_HEIGHT],
	);

	// Handle hour increment/decrement
	const handleHoursIncrement = () => {
		const newHours = hours === 23 ? 0 : hours + 1;
		onHoursChange(newHours);
	};

	const handleHoursDecrement = () => {
		const newHours = hours === 0 ? 23 : hours - 1;
		onHoursChange(newHours);
	};

	// Handle minute increment/decrement
	const handleMinutesIncrement = () => {
		const newMinutes = (minutes + 1) % 60;
		onMinutesChange(newMinutes);
	};

	const handleMinutesDecrement = () => {
		const newMinutes = minutes === 0 ? 59 : minutes - 1;
		onMinutesChange(newMinutes);
	};

	// Handle minute +5/-5
	const handleMinutesIncrementBy5 = () => {
		const newMinutes = (minutes + 5) % 60;
		onMinutesChange(newMinutes);
	};

	const handleMinutesDecrementBy5 = () => {
		const newMinutes = minutes < 5 ? 60 - (5 - minutes) : minutes - 5;
		onMinutesChange(newMinutes);
	};

	// Handle keyboard navigation for hours
	const handleHoursKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'ArrowUp') {
			e.preventDefault();
			handleHoursIncrement();
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			handleHoursDecrement();
		}
	};

	// Handle keyboard navigation for minutes
	const handleMinutesKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (e.shiftKey) {
				handleMinutesIncrementBy5();
			} else {
				handleMinutesIncrement();
			}
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (e.shiftKey) {
				handleMinutesDecrementBy5();
			} else {
				handleMinutesDecrement();
			}
		}
	};

	// Handle scroll events to update selected values
	const handleHoursScroll = () => {
		if (hoursScrollRef.current) {
			markScrolling();
			const scrollTop = hoursScrollRef.current.scrollTop;
			const index = Math.round(scrollTop / DIGIT_HEIGHT);
			const normalizedIndex = normalizeIndex(index, hoursArray.length);
			const newHours = hoursArray[normalizedIndex];
			if (newHours !== undefined && newHours !== hours) {
				onHoursChange(newHours);
			}
		}
	};

	const handleMinutesScroll = () => {
		if (minutesScrollRef.current) {
			markScrolling();
			const scrollTop = minutesScrollRef.current.scrollTop;
			const index = Math.round(scrollTop / DIGIT_HEIGHT);
			const normalizedIndex = normalizeIndex(index, minutesArray.length);
			const newMinutes = minutesArray[normalizedIndex];
			if (newMinutes !== undefined && newMinutes !== minutes) {
				onMinutesChange(newMinutes);
			}
		}
	};

	useEffect(() => {
		if (isScrolling) return;
		const targetIndex = getNearestIndex(
			hours,
			hoursArray.length,
			HOURS_REPEAT_COUNT,
			hoursScrollRef,
			middleHoursOffset,
		);
		const behavior = targetIndex.distance > hoursArray.length / 2 ? 'auto' : 'smooth';
		scrollToIndex(hoursScrollRef, targetIndex.index, behavior);
	}, [hours, hoursArray, isScrolling, middleHoursOffset, getNearestIndex, scrollToIndex]);

	useEffect(() => {
		if (isScrolling) return;
		const targetIndex = getNearestIndex(
			minutes,
			minutesArray.length,
			MINUTES_REPEAT_COUNT,
			minutesScrollRef,
			middleMinutesOffset,
		);
		const behavior = targetIndex.distance > minutesArray.length / 2 ? 'auto' : 'smooth';
		scrollToIndex(minutesScrollRef, targetIndex.index, behavior);
	}, [minutes, minutesArray, isScrolling, middleMinutesOffset, getNearestIndex, scrollToIndex]);

	useEffect(() => {
		if (hasInitializedRef.current) return;
		if (!hoursScrollRef.current || !minutesScrollRef.current) return;

		const initialHoursIndex = middleHoursOffset + hoursArray.indexOf(hours);
		const initialMinutesIndex = middleMinutesOffset + minutesArray.indexOf(minutes);

		scrollToIndex(hoursScrollRef, initialHoursIndex, 'auto');
		scrollToIndex(minutesScrollRef, initialMinutesIndex, 'auto');
		hasInitializedRef.current = true;
	}, [hours, minutes, hoursArray, minutesArray, middleHoursOffset, middleMinutesOffset, scrollToIndex]);

	useEffect(
		() => () => {
			clearScrollTimeout();
		},
		[],
	);

	return (
		<div className="digital-clock-answer">
			<div className="digital-clock-answer__background">
				<BackgroundComponent />
			</div>

			<div className="digital-clock-answer__content">
				{/* Hours Column */}
				<div className="digital-clock-answer__column">
					<Button
						type="primary"
						color="purple"
						title="-"
						onClick={handleHoursDecrement}
						btnType="button"
						className="digital-clock-answer__button"
					/>

					<div className="digital-clock-answer__scroll-container">
						<div
							className="digital-clock-answer__scroll-inner"
							ref={hoursScrollRef}
							onScroll={handleHoursScroll}
							onKeyDown={handleHoursKeyDown}
							tabIndex={0}
							role="listbox"
							aria-label="Hours"
						>
							<div className="digital-clock-answer__scroll-content">
								{repeatedHours.map((hour, index) => (
									<div
										key={`${hour}-${index}`}
										className={`digital-clock-answer__digit ${
											hour === hours ? 'digital-clock-answer__digit--active' : ''
										}`}
										role="option"
										aria-selected={hour === hours}
									>
										{String(hour).padStart(2, '0')}
									</div>
								))}
							</div>
						</div>
					</div>

					<Button
						type="primary"
						color="purple"
						title="+"
						onClick={handleHoursIncrement}
						btnType="button"
						className="digital-clock-answer__button"
					/>
				</div>

				{/* Separator */}
				<div className="digital-clock-answer__separator">:</div>

				{/* Minutes Column */}
				<div className="digital-clock-answer__column">
					<div className="digital-clock-answer__button-group">
						<Button
							type="primary"
							color="purple"
							title="-"
							onClick={handleMinutesDecrement}
							btnType="button"
							className="digital-clock-answer__button"
						/>
						<Button
							type="primary"
							color="purple"
							title="- 5"
							onClick={handleMinutesDecrementBy5}
							btnType="button"
							className="digital-clock-answer__button digital-clock-answer__button--small"
						/>
					</div>

					<div className="digital-clock-answer__scroll-container">
						<div
							className="digital-clock-answer__scroll-inner"
							ref={minutesScrollRef}
							onScroll={handleMinutesScroll}
							onKeyDown={handleMinutesKeyDown}
							tabIndex={0}
							role="listbox"
							aria-label="Minutes"
						>
							<div className="digital-clock-answer__scroll-content">
								{repeatedMinutes.map((minute, index) => (
									<div
										key={`${minute}-${index}`}
										className={`digital-clock-answer__digit ${
											minute === minutes ? 'digital-clock-answer__digit--active' : ''
										}`}
										role="option"
										aria-selected={minute === minutes}
									>
										{String(minute).padStart(2, '0')}
									</div>
								))}
							</div>
						</div>
					</div>

					<div className="digital-clock-answer__button-group">
						<Button
							type="primary"
							color="purple"
							title="+"
							onClick={handleMinutesIncrement}
							btnType="button"
							className="digital-clock-answer__button"
						/>
						<Button
							type="primary"
							color="purple"
							title="+ 5"
							onClick={handleMinutesIncrementBy5}
							btnType="button"
							className="digital-clock-answer__button digital-clock-answer__button--small"
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DigitalClockAnswer;
